import { Link } from 'react-router-dom';
import type { WorkshopDto } from '@workshop-hst/shared';
import { dateParts, formatMonthDayNumeric, timeOnly, relativeFromNow } from '../lib/format';
import { Tag } from './Tag';
import { Avatar } from './Avatar';
import { GuestRow } from './GuestBadge';

export function WorkshopCard({ workshop, variant }: { workshop: WorkshopDto; variant: 'upcoming' | 'past' }) {
  const { day, month } = dateParts(workshop.startsAt);

  return (
    <Link to={`/w/${workshop.slug}`} className="card group">
      <div className="relative aspect-[16/10] overflow-hidden">
        {workshop.coverImageUrl ? (
          <img
            src={workshop.coverImageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-accent-soft to-canvas" />
        )}
        {/* Date pill top-left */}
        <div className="absolute left-3 top-3 inline-flex items-center gap-2 rounded-xl bg-white/95 px-2.5 py-1.5 shadow-card backdrop-blur">
          <span className="text-[10px] uppercase tracking-wider text-ink-subtle">{month}</span>
          <span className="text-base font-semibold leading-none">{day}</span>
        </div>
        {/* Status pill top-right */}
        {variant === 'past' && workshop.recordingAvailable ? (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/65 px-2 py-1 text-[11px] font-medium text-white backdrop-blur">
            <PlayDot /> Recording
          </span>
        ) : variant === 'upcoming' ? (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-[11px] font-medium text-ink-muted backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            {relativeFromNow(workshop.startsAt)}
          </span>
        ) : null}
        {/* Tag chip bottom-left (max 1) */}
        {workshop.tags.length > 0 ? (
          <div className="absolute bottom-3 left-3">
            <Tag>{workshop.tags[0]!}</Tag>
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          <span>{formatMonthDayNumeric(workshop.startsAt)}</span>
          <span className="h-1 w-1 rounded-full bg-line-strong" />
          <span>{timeOnly(workshop.startsAt)}</span>
          <span className="h-1 w-1 rounded-full bg-line-strong" />
          <span>{workshop.durationMin} min</span>
        </div>
        {/* Title always rendered as real HTML, regardless of whether the cover
            image has it baked in. Reasons: (a) accessibility / SEO — search
            crawlers and screen readers need real text, not text inside a PNG;
            (b) legacy covers generated before the title-baked-in prompt
            rewrite don't actually contain the title, and hiding the HTML
            would leave those cards anonymous; (c) the visual duplication on
            new covers is mild — image art on top, functional header below. */}
        <h3 className="text-[17px] font-semibold leading-snug tracking-tight">{workshop.title}</h3>
        {workshop.description ? (
          <p className="text-[13.5px] leading-relaxed text-ink-muted line-clamp-2">{workshop.description}</p>
        ) : null}
        <div className="mt-auto flex items-end justify-between gap-3 pt-2">
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Avatar name={workshop.hostName} url={workshop.hostHeadshotUrl} size={22} />
              <span className="text-xs font-medium text-ink-muted truncate">{workshop.hostName}</span>
            </div>
            {workshop.guest ? <GuestRow guest={workshop.guest} size="sm" /> : null}
          </div>
          <span className={variant === 'upcoming' ? 'text-xs font-semibold text-accent shrink-0' : 'text-xs font-medium text-ink-muted shrink-0'}>
            {variant === 'upcoming' ? 'Register →' : workshop.recordingAvailable ? 'Watch →' : 'Coming soon'}
          </span>
        </div>
      </div>
    </Link>
  );
}

function PlayDot() {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" aria-hidden>
      <path d="M0 0v8l8-4-8-4z" />
    </svg>
  );
}
